import { memo } from 'react';
import { useGetModuleByIdQuery } from 'store/services/core/modules';
import type { AssistantResponse } from 'types';
import { getChatbotHistoryScope } from 'utils/chatbotHistory';
import { useLogin } from 'contexts';
import { ChatbotWidget } from '../Chatbot/ChatbotWidget';
import { useUserChatbotModuleId } from './hooks/useUserChatbotModuleId';

const EMPTY_ASSISTANTS: readonly AssistantResponse[] = [];

type UserChatbotMode = 'auto' | 'module' | 'default';

interface UserChatbotProps {
  mode?: UserChatbotMode;
}

const UserChatbotBase = ({ mode = 'auto' }: UserChatbotProps) => {
  const routeModuleId = useUserChatbotModuleId();
  const isModuleRoute = routeModuleId !== undefined;
  const isModuleChatbot = mode !== 'default' && isModuleRoute;
  const isDefaultChatbot = mode !== 'module' && !isModuleRoute;
  const shouldRender = isModuleChatbot || isDefaultChatbot;
  const moduleId = isModuleChatbot ? routeModuleId : undefined;
  const { user } = useLogin();
  const { currentData: moduleData, isFetching: isModuleFetching } = useGetModuleByIdQuery({ id: moduleId ?? '' }, { skip: !moduleId });
  const isModuleInitialLoading = Boolean(moduleId && isModuleFetching && moduleData === undefined);
  const assistants = moduleId ? (moduleData?.assistants ?? EMPTY_ASSISTANTS) : EMPTY_ASSISTANTS;
  if (!shouldRender || !user?.tenant_id || !user.user_id) return null;

  const historyScope = getChatbotHistoryScope('user', { tenantId: user.tenant_id, userId: user.user_id }, moduleId);

  if (isModuleInitialLoading || (moduleId !== undefined && assistants.length === 0)) return null;

  return (
    <ChatbotWidget
      key={historyScope}
      assistants={assistants}
      historyScope={historyScope}
      isAssistantsLoading={isModuleInitialLoading}
      showAssistantPicker={assistants.length > 1}
      toggleButtonTestId="user-toolbar-chatbot-button"
      fallbackAssistantId={assistants[0]?.id}
    />
  );
};

export const UserChatbot = memo(UserChatbotBase);
